"use client";

import { useEffect, useMemo, useState } from "react";
import { Link, usePathname, useRouter, useSearchParams } from "@/src/lib/next-compat";
import { apiFetch } from "@/src/lib/api-fetch";
import { AlertTriangle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import type { LearnerCostPage } from "@/lib/llm-models/costs";
import {
  learnerQueryParams,
  NONE_KEY,
  parseCostRange,
  parseLearnerQuery,
  type CostRange,
  type LearnerQuery,
  type LearnerSort,
} from "@/lib/llm-models/cost-filters";
import { FilterListbox, focusRing, SearchBox } from "./CostFilterControls";
import { RangePicker, rangePhrase, withRange } from "./CostRangePicker";
import { costUsd, formatPakistanTime, plural, tokens } from "./format";

async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await apiFetch(url);
  if (res.status === 401) throw new Error("Your session has expired. Sign in again.");
  if (res.status === 403) throw new Error("Only inaraX admins can see AI costs.");
  if (res.status === 400) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? `Failed to load ${what}.`);
  if (!res.ok) throw new Error(`Failed to load ${what}.`);
  return (await res.json()) as T;
}

const COLUMNS: { sort: LearnerSort; label: string }[] = [
  { sort: "courses", label: "Courses" },
  { sort: "calls", label: "Calls" },
  { sort: "tokensIn", label: "Input tokens" },
  { sort: "tokensOut", label: "Output tokens" },
  { sort: "cost", label: "Spend" },
  { sort: "lastAt", label: "Last activity" },
];

const checkLearnerSearch = (v: string) => (v.length > 100 ? "Enter at most 100 characters." : null);

function SortHeader({ column, query, onSort }: { column: (typeof COLUMNS)[number]; query: LearnerQuery; onSort: (sort: LearnerSort) => void }) {
  const active = query.sort === column.sort;
  const Arrow = query.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="px-3 py-2 text-right font-semibold" aria-sort={active ? (query.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(column.sort)}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded ${active ? "text-slate-900" : "hover:text-slate-700"} ${focusRing}`}
      >
        {column.label}
        {active ? <Arrow className="h-3 w-3" /> : null}
      </button>
    </th>
  );
}

/**
 * Every learner's AI spend, highest first, with its own range, course filter and email search.
 * Learners only: work started from the admin side isn't anyone's. Selecting a learner opens the
 * Costs tab narrowed to them.
 */
export default function LearnerCostsClient() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // The URL holds the range, filters, sort and page, so a view can be shared or reloaded.
  const queryString = searchParams.toString();
  const { range, query } = useMemo(() => {
    const params = new URLSearchParams(queryString);
    return { range: parseCostRange(params).range, query: parseLearnerQuery(params).query };
  }, [queryString]);
  const apiQuery = useMemo(() => withRange(learnerQueryParams(query), range).toString(), [range, query]);
  const rangeQuery = useMemo(() => withRange(new URLSearchParams(), range).toString(), [range]);

  const [result, setResult] = useState<{ query: string; data: LearnerCostPage | null; error: string | null } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getJson<LearnerCostPage>(`/api/admin/ai-models/costs/learners?${apiQuery}`, "learner costs")
      .then((data) => {
        if (!cancelled) setResult({ query: apiQuery, data, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) setResult({ query: apiQuery, data: null, error: e instanceof Error ? e.message : "Failed to load learner costs." });
      });
    return () => {
      cancelled = true;
    };
  }, [apiQuery, attempt]);

  // The course filter's options depend only on the range, so paging and sorting don't refetch them.
  const [courses, setCourses] = useState<{ key: string; label: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    getJson<{ courses: { key: string; label: string }[] }>(`/api/admin/ai-models/costs/learners/courses?${rangeQuery}`, "courses")
      .then((body) => {
        if (!cancelled) setCourses(body.courses);
      })
      // The table reports load errors; an empty course list is enough here.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rangeQuery]);

  // Keep showing the last page while the next one loads, so the table doesn't jump.
  const [shown, setShown] = useState<LearnerCostPage | null>(null);
  if (result?.data && result.data !== shown) setShown(result.data);
  const loading = result?.query !== apiQuery;
  const error = !loading ? (result?.error ?? null) : null;

  const navigate = (nextRange: CostRange, next: LearnerQuery) => {
    const params = learnerQueryParams(next);
    params.set("tab", "learners");
    // The default 30 days stays out of the URL.
    if (!("period" in nextRange && nextRange.period === "30d")) withRange(params, nextRange);
    router.replace(`${pathname}?${params}`, { scroll: false });
  };
  /** Any change but paging starts again from the first page. */
  const update = (patch: Partial<LearnerQuery>) => navigate(range, { ...query, page: 1, ...patch });
  const sortBy = (sort: LearnerSort) =>
    update(sort === query.sort ? { dir: query.dir === "asc" ? "desc" : "asc" } : { sort, dir: "desc" });

  /** The Costs tab, narrowed to one learner over the same range and course, so its totals match the row. */
  const costsHref = (learner: string) => {
    const params = new URLSearchParams({ tab: "costs", learner });
    if (query.course !== undefined) params.set("course", String(query.course));
    if (!("period" in range && range.period === "30d")) withRange(params, range);
    return `${pathname}?${params}`;
  };

  const page = shown;
  const firstRow = page ? (page.page - 1) * page.pageSize : 0;
  const lastPage = page ? Math.max(1, Math.ceil(page.total / page.pageSize)) : 1;
  const filtered = query.course !== undefined || !!query.search;

  const retry = () => {
    setResult(null);
    setAttempt((n) => n + 1);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <RangePicker range={range} onChange={(next) => navigate(next, { ...query, page: 1 })} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FilterListbox
            label="Course"
            value={query.course === undefined ? "" : String(query.course)}
            onChange={(v) => update({ course: !v ? undefined : v === NONE_KEY ? NONE_KEY : Number(v) })}
            options={courses}
          />
          <SearchBox
            id="learner-search"
            label="Find a learner"
            placeholder="Type an email"
            value={query.search ?? ""}
            check={checkLearnerSearch}
            onSubmit={(search) => update({ search })}
          />
        </div>
      </div>

      {error && !page ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-slate-900">Could not load learner costs</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{error}</p>
          <button
            type="button"
            onClick={retry}
            className={`mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 ${focusRing}`}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      ) : !page ? (
        <div className="h-96 animate-pulse rounded-xl bg-white shadow-sm ring-1 ring-slate-200" aria-busy="true">
          <span className="sr-only">Loading learner costs…</span>
        </div>
      ) : (
        <section className={`rounded-xl bg-white shadow-sm ring-1 ring-slate-200 transition-opacity ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
          <div className="px-5 pt-4">
            <h2 className="text-sm font-semibold text-slate-900">Spend by learner</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {page.total > 0
                ? `${plural(page.total, "learner")} spent ${costUsd(page.totalCost)} ${rangePhrase(range)}. Select a learner to see their costs in detail.`
                : "What each learner's own lessons cost. Work started from the admin side, like translations and quizzes, isn't counted."}
            </p>
          </div>
          {error ? (
            <div className="mx-5 mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{error} The table is from before this change.</span>
              <button
                type="button"
                onClick={retry}
                className={`inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm ring-1 ring-red-200 hover:bg-red-50 ${focusRing}`}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </button>
            </div>
          ) : null}

          {page.rows.length === 0 ? (
            <div className="px-5 pb-5 pt-3 text-sm text-slate-500">
              <p>
                {page.page > 1 && page.total > 0
                  ? "This page is past the last learner."
                  : filtered
                    ? "No learners match."
                    : `No learner made AI calls ${rangePhrase(range)}.`}
              </p>
              {page.page > 1 && page.total > 0 ? (
                <button
                  type="button"
                  onClick={() => update({})}
                  className={`mt-2 rounded text-sm font-semibold text-indigo-600 hover:text-indigo-800 ${focusRing}`}
                >
                  Go to the first page
                </button>
              ) : filtered ? (
                <button
                  type="button"
                  onClick={() => update({ course: undefined, search: undefined })}
                  className={`mt-2 rounded text-sm font-semibold text-indigo-600 hover:text-indigo-800 ${focusRing}`}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <div className="pb-3 pt-3">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] text-left text-sm">
                  <thead>
                    <tr className="whitespace-nowrap border-y border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-500">
                      <th className="w-10 py-2 pl-5 pr-1 text-right font-semibold">#</th>
                      <th className="px-3 py-2 font-semibold">Learner</th>
                      {COLUMNS.map((c) => (
                        <SortHeader key={c.sort} column={c} query={query} onSort={sortBy} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {page.rows.map((r, i) => (
                      <tr key={r.key} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pl-5 pr-1 text-right text-xs tabular-nums text-slate-400">{firstRow + i + 1}</td>
                        <td className="max-w-[18rem] px-3 py-2">
                          <Link
                            href={costsHref(r.key)}
                            title={`See ${r.label}'s costs`}
                            className={`block max-w-full truncate rounded font-medium text-slate-800 hover:text-indigo-700 ${focusRing}`}
                          >
                            {r.label}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.courses.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.calls.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700" title={r.tokensIn.toLocaleString()}>
                          {tokens(r.tokensIn)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700" title={r.tokensOut.toLocaleString()}>
                          {tokens(r.tokensOut)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{costUsd(r.cost)}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-slate-500">{formatPakistanTime(r.lastAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-3 text-xs text-slate-500">
                <span>
                  Showing {(firstRow + 1).toLocaleString()}–{(firstRow + page.rows.length).toLocaleString()} of {page.total.toLocaleString()}
                </span>
                {lastPage > 1 ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={page.page <= 1}
                      onClick={() => navigate(range, { ...query, page: page.page - 1 })}
                      className={`inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" /> Previous
                    </button>
                    <span className="tabular-nums">
                      Page {page.page} of {lastPage}
                    </span>
                    <button
                      type="button"
                      disabled={page.page >= lastPage}
                      onClick={() => navigate(range, { ...query, page: page.page + 1 })}
                      className={`inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
                    >
                      Next <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
